import { useCallback, useMemo } from 'react';
import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import { PagedQueryOptions } from '@elementary/common-ui/hooks/useGraphqlPagedQuery';
import { TestExecutionField } from '@elementary/common-ui/services/gql/types';
import { parseGraphqlFilterOptions } from '@elementary/common-ui/utils/graphqlFilters';
import {
  TEST_RUNS,
  TEST_RUNS_FILTER_OPTIONS,
} from '@elementary/common-ui/constants/queryKeys';
import { DEFAULT_DAYS_BACK, PAGE_SIZE } from '@elementary/common-ui/constants';
import { TEST_RUNS_GROUP_BY } from '@elementary/common-ui/constants/urlParams';
import { type ApiTestRunData } from '@elementary/common-ui/pages';
import useGroupByParam from 'shared/hooks/useGroupByParam';
import useGroupedTableData from 'shared/hooks/useGroupedTableData';
import { formatGroupTitle, getGroupValueFilter } from 'shared/utils/utils';
import {
  getTestRunsFilterOptionsQuery,
  getTestRunsTableDataQuery,
} from '../api';
import {
  ApiTestRunsFilterOptionsResponse,
  ApiTestRunsTableDataPagedResponse,
} from '../types';
import {
  COLUMN_TO_FIELD_MAP,
  FIELD_TO_COLUMN_MAP,
  GROUP_TITLE_FORMATTERS,
  GROUPABLE_COLUMN_IDS,
} from '../consts';
import { mapTestRunRows } from '../utils';
import useTestRunsQueryContext from './useTestRunsQueryContext';

type Args = {
  search: string;
};

const useGroupedTestRuns = ({ search }: Args) => {
  const { groupBy, onGroupByChange } = useGroupByParam({
    paramKey: TEST_RUNS_GROUP_BY,
    groupableColumnIds: GROUPABLE_COLUMN_IDS,
  });
  const isGrouped = !!groupBy;

  const {
    envid,
    selectedAssetIds,
    selectedTestIds,
    selectedIdsHash,
    hasSelectedNodes,
    urlFiltersDebounced,
    urlSort,
    testExecutionFilters,
    sort,
    filterOptions,
  } = useTestRunsQueryContext();

  const groupEnabled = isGrouped && hasSelectedNodes;
  const field = groupBy ? COLUMN_TO_FIELD_MAP[groupBy] : undefined;
  const searchValue = search || null;

  const groupableColumnIds = useMemo(
    () =>
      GROUPABLE_COLUMN_IDS.filter(
        (columnId) => (filterOptions[columnId]?.length ?? 0) > 0,
      ),
    [filterOptions],
  );

  const { data: groupOptionsData, isFetching: isGroupOptionsFetching } =
    useGraphqlQuery<ApiTestRunsFilterOptionsResponse>({
      queryKey: [
        TEST_RUNS_FILTER_OPTIONS,
        envid,
        selectedIdsHash,
        DEFAULT_DAYS_BACK,
        'grouped',
        urlFiltersDebounced,
        searchValue,
      ],
      query: getTestRunsFilterOptionsQuery,
      variables: {
        testIds: selectedTestIds.length ? selectedTestIds : null,
        assetIds: selectedAssetIds.length ? selectedAssetIds : null,
        daysBack: DEFAULT_DAYS_BACK,
        search: searchValue,
        testExecutionFilters,
      },
      enabled: groupEnabled,
      envId: envid,
      keepPreviousData: true,
    });

  const groupValues = useMemo(() => {
    if (!groupBy) return [];
    const parsed = parseGraphqlFilterOptions<TestExecutionField>(
      groupOptionsData?.latestTestExecutionsFilterOptions || [],
      FIELD_TO_COLUMN_MAP,
    );
    return parsed[groupBy] ?? [];
  }, [groupOptionsData, groupBy]);

  const nonEmptyGroupValues = useMemo(
    () => groupValues.map(({ value }) => value).filter(Boolean),
    [groupValues],
  );

  const formatTitle = useCallback(
    (value: string) =>
      groupBy
        ? formatGroupTitle(GROUP_TITLE_FORMATTERS, groupBy, value)
        : value,
    [groupBy],
  );

  const buildQueryOptions = useCallback(
    (
      value: string,
    ): PagedQueryOptions<ApiTestRunData, ApiTestRunsTableDataPagedResponse> => {
      const groupFilter = field
        ? getGroupValueFilter(field, value, nonEmptyGroupValues)
        : undefined;
      return {
        queryKey: [
          TEST_RUNS,
          envid,
          selectedIdsHash,
          DEFAULT_DAYS_BACK,
          urlFiltersDebounced,
          urlSort,
          searchValue,
          groupFilter,
        ],
        query: getTestRunsTableDataQuery,
        variables: {
          testIds: selectedTestIds.length ? selectedTestIds : null,
          assetIds: selectedAssetIds.length ? selectedAssetIds : null,
          daysBack: DEFAULT_DAYS_BACK,
          testExecutionFilters: groupFilter
            ? [...testExecutionFilters, groupFilter]
            : testExecutionFilters,
          sort,
          search: searchValue,
          limit: PAGE_SIZE,
        },
        select: (data) => data.latestTestExecutionsPaged,
        enabled: groupEnabled && !!groupFilter,
        envId: envid,
      };
    },
    [
      field,
      nonEmptyGroupValues,
      envid,
      selectedIdsHash,
      urlFiltersDebounced,
      urlSort,
      searchValue,
      selectedTestIds,
      selectedAssetIds,
      testExecutionFilters,
      sort,
      groupEnabled,
    ],
  );

  const {
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    isAnyFetchingWithRows,
  } = useGroupedTableData({
    groupBy,
    groupValues,
    enabled: !!field,
    formatGroupTitle: formatTitle,
    buildQueryOptions,
    mapItems: mapTestRunRows,
  });

  const tableData = useMemo(
    () => groupedData.flatMap((group) => group.data),
    [groupedData],
  );

  return {
    groupBy,
    onGroupByChange,
    isGrouped,
    groupableColumnIds,
    groupFetchers,
    groupedData,
    expandedGroups,
    onGroupToggle,
    onGroupScrollToBottom,
    tableData,
    isFetching: isGroupOptionsFetching || isAnyFetchingWithRows,
  };
};

export default useGroupedTestRuns;
